import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import { AlertConstants, useAlerts } from "best-common-react";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import { LocalStorageConstants } from "../constants/LocalStorageConstants";
import { useUser } from "../contexts/UserContext";

interface HttpInterceptorProps {
  children: React.ReactNode;
}

const HttpInterceptor: React.FC<HttpInterceptorProps> = ({ children }) => {
  const { logout } = useUser();
  const { addAlert } = useAlerts();
  const [alertMillis, setAlertMillis] = useState<number>(0);
  const [alertThresholdMillis, setAlertThresholdMillis] = useState<number>(0);

  useEffect(() => {
    if (alertMillis > alertThresholdMillis) {
      setAlertThresholdMillis(alertMillis + 1000);
      addAlert({
        type: AlertConstants.TYPES.DANGER,
        text: "An unexpected error occurred relating to your user configuration, please contact support.",
      });
    }
  }, [alertMillis, alertThresholdMillis, setAlertThresholdMillis]);

  axiosRetry(axios, {
    retries: 3, //attempts to retry
    retryCondition: (error) => {
      console.log("Error fetching from API will retry...");
      return !!error?.response && error.response.status === 401;
    }, // only retry on unauthenticated errors to prevent retrying 500s unless that is wanted
    retryDelay: (retryCount) => retryCount * 1000, // wait 1 second between retrys
  });

  axios.interceptors.request.use((config: AxiosRequestConfig) => {
    const oktaTokenStr =
      localStorage.getItem(LocalStorageConstants.OKTA_TOKEN) || '{"accessToken": "", "idToken": "" }';

    const { accessToken = {}, idToken = {} } = JSON.parse(oktaTokenStr);

    if (config.url.indexOf("/oauth/token") < 0 || config.headers.addToken) {
      config.headers.Authorization = `Bearer ${accessToken.accessToken}`;
      config.headers["x-id-token"] = idToken.idToken;
    }
    return config;
  });

  axios.interceptors.response.use(
    (resp: AxiosResponse) => {
      if (!resp) return null;
      return resp;
    },
    (err: AxiosError) => {
      if (err.response?.status === 401) {
        logout();
      } else if (err.response?.status === 403) {
        // update state to consider triggering a Forbidden alert
        // the indirection is necessary because this block can sometimes execute multiple times for a single XHR
        logout();
        setAlertMillis(DateTime.now().toMillis());
      }
      return Promise.reject(err);
    },
  );

  return <>{children}</>;
};

export default HttpInterceptor;
